<template>
  <div
    class="flex h-full flex-col overflow-y-auto"
    :class="[
      isExpanded && 'w-[320px] gap-4 px-4 2xl:w-[400px]',
      !isExpanded && !showCollapsedSidebar && 'w-0',
      !isExpanded && showCollapsedSidebar && 'w-20 gap-2 px-4',
    ]"
  >
    <ObjectivesList
      v-if="showObjectiveSection"
      :dialog-phase="dialog.phase"
      :dialog-id="dialog.id"
      :created-objectives="objectiveData?.createdObjectives ?? []"
      :ongoing-objectives="objectiveData?.ongoingObjectives ?? []"
      :show-form="showObjectiveForm"
      :creatable-impulse="creatableImpulse"
      :are-impulses-read-only="areImpulsesReadOnly"
      :is-expanded="isExpanded"
      :can-create-impulse="canCreateImpulse"
      @create:impulse="createObjective"
      @cancel:create="cancelCreateObjective"
      @save:impulse="saveImpulse"
    />
    <div
      v-if="isExpanded && showCommentSection && showObjectiveSection"
      class="border-outline-variant border-t"
    ></div>
    <CommentsList
      v-if="showCommentSection"
      :can-create-comments="canCreateComment"
      :can-edit-comments="isEditable"
      :can-promote-to-conversation-note="
        isCoach && dialog.phase === DialogPhase.Conversation
      "
      :comment-groups="displayedCommentGroups"
      :show-form="showCommentForm"
      :creatable-comment="creatableComment"
      :is-expanded="isExpanded"
      :dialog-phase="dialog.phase"
      :conversation-note-groups="conversationNoteGroups"
      @update:comment="updateComment"
      @delete:comment="deleteComment"
      @create:comment="createComment"
      @cancel:create="cancelCreateComment"
      @save:comment="saveComment"
    />
  </div>
</template>
<script lang="ts" setup>
  import ObjectivesList from '@/core/components/objective/ObjectivesList.vue'
  import CommentsList from '../../../core/components/comment/CommentsList.vue'
  import { useComments } from '@/core/composables/use-comments'
  import { useObjectives } from '@/core/composables/use-objectives'
  import { computed, inject, ref } from 'vue'
  import { DialogPhase, type Dialog } from '../../../core/models/dialog/dialog'
  import { useUserStore } from '../../../stores/user.store'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import { hideUnsharedCommentsKey } from '../composable/use-dialog-navigation'

  interface Props {
    coacheeId: string
    dialogSlideId?: string
    isEditable?: boolean
    dialog: Dialog
  }

  const props = defineProps<Props>()

  const isDialogConducted = computed(() => {
    return props.dialog.phase === DialogPhase.Conducted || props.dialog.conductedAt !== undefined
  })

  const {
    commentGroups,
    status: commentStatus,
    saveComment,
    createComment,
    creatableComment,
    showForm: showCommentForm,
    cancelCreateComment,
    updateComment,
    deleteComment,
    conversationNoteGroups,
  } = useComments(() => ({ dialogId: props.dialog.id, dialogSlideId: props.dialogSlideId }))

  const hideUnsharedComments = inject(hideUnsharedCommentsKey, ref(false))

  const shouldHideUnsharedComments = computed(() => {
    return (
      hideUnsharedComments.value &&
      (props.dialog.phase === DialogPhase.Conversation ||
        props.dialog.phase === DialogPhase.Conducted)
    )
  })

  const displayedCommentGroups = computed(() => {
    if (!shouldHideUnsharedComments.value) {
      return commentGroups.value
    }
    return commentGroups.value
      .map((group) => group.filter((comment) => comment.isShared))
      .filter((group) => group.length > 0)
  })

  const isExpanded = computed(() => {
    return (
      commentGroups.value.length > 0 ||
      conversationNoteGroups.value.length > 0 ||
      totalNumberOfObjectives.value > 0 ||
      showObjectiveForm.value ||
      showCommentForm.value
    )
  })

  const {
    createObjective,
    saveImpulse,
    cancelCreateObjective,
    creatableImpulse,
    showForm: showObjectiveForm,
    data: objectiveData,
    totalNumberOfObjectives,
    status: objectiveStatus,
  } = useObjectives(() => ({
    ownerId: props.coacheeId,
    feedbackTopicId: props.dialogSlideId,
    dialogId: props.dialog.id,
  }))

  const { user } = useUserStore()

  const isCoach = computed(() => {
    return props.dialog.coach.id == user.id
  })

  const canCreateImpulse = computed(() => {
    if (isDialogConducted.value) {
      return false
    }
    if (isCoach.value) {
      return true
    }
    return (
      props.dialog.phase === DialogPhase.Feedback || props.dialog.phase === DialogPhase.Conversation
    )
  })

  const areImpulsesReadOnly = computed(() => {
    if (isDialogConducted.value) {
      return true
    }
    return !isCoach.value && props.dialog.phase === DialogPhase.CoachPreparation
  })

  const showCollapsedSidebar = computed(() => {
    return !!props.isEditable || canCreateImpulse.value
  })

  const canCreateComment = computed(() => {
    if (props.dialog.conductedAt) {
      return false
    }
    return isCoach.value || props.dialog.coacheeDataReleasedAt === undefined
  })

  const showCommentSection = computed(() => {
    if (commentStatus.value !== ApiRequestStatus.Success) {
      return false
    }
    return canCreateComment.value || commentGroups.value.length > 0
  })

  const showObjectiveSection = computed(() => {
    if (objectiveStatus.value !== ApiRequestStatus.Success) {
      return false
    }
    return canCreateImpulse.value || totalNumberOfObjectives.value > 0
  })
</script>
